import { useCallback, useContext, useEffect, useState } from "react"

import { Link, useLocalSearchParams } from "expo-router"

import { Asset, Index, Instrument, InstrumentCollection } from "wealthmode-frontend/backend"
import { Box, Button, ButtonText, HStack, Text, VStack } from "wealthmode-frontend/gluestack/ui"

import { DetailField, ResourceDetail } from "@/components"
import { AssetLinkModal } from "@/components/widgets"
import { BackendContext } from "@/context"

export default function Instruments() {
  const backend = useContext(BackendContext)
  const { instrument_id } = useLocalSearchParams<{ instrument_id: string }>()
  const [instrument, setInstrument] = useState<Instrument>()
  const [asset, setAsset] = useState<Asset>()
  const [saving, setSaving] = useState(false)
  const [linkOpen, setLinkOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    var data = backend.link("data").resolve() as Index
    await data.fetch({ reload: true })

    var instruments = data.link("instruments")?.resolve() as InstrumentCollection | undefined
    if (!instruments) {
      return undefined
    }

    const ins = instruments.model(instrument_id)
    await ins.fetch({
      reload: true,
    })

    const ass = ins.link("asset")?.resolve()
    await ass?.fetch({
      reload: false,
    })

    setInstrument(ins)
    setAsset(ass)
  }, [backend, instrument_id])

  useEffect(() => {
    load()
  }, [load])

  async function curate(curation: { asset_blocked?: boolean; asset_id?: string }) {
    const self = instrument?.link("self")
    if (!self) {
      throw new Error("Instrument resource is not available")
    }

    const response = await self.send({
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(curation),
    })
    if (!response.ok) {
      const text = await response.text()
      throw new Error(text || response.statusText)
    }

    await load()
  }

  async function toggleAssetBlocked() {
    if (!instrument) {
      return
    }

    setSaving(true)
    setError(null)

    try {
      await curate({ asset_blocked: !instrument.attributes.asset_blocked })
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  async function linkAsset(assetID: string) {
    await curate({ asset_id: assetID })
  }

  const fields: DetailField[] = []

  if (instrument) {
    fields.push(
      { label: "Symbol", value: instrument.attributes.symbol ?? "" },
      { label: "Type", value: instrument.attributes.type },
      { label: "Subtype", value: instrument.attributes.subtype },
      { label: "Market", value: instrument.attributes.market },
      {
        label: "Asset",
        value: asset ? (
          <Link href={`/assets/${asset.id}`}>
            <Text className="text-markdown-link hover:opacity-80 underline">
              {asset.attributes.name} - {asset.attributes.symbol}
            </Text>
          </Link>
        ) : (
          <HStack>
            <Button
              size="xs"
              action="secondary"
              onPress={() => setLinkOpen(true)}
            >
              <ButtonText>Link</ButtonText>
            </Button>
          </HStack>
        ),
      },
    )

    if (instrument.attributes.asset_blocked != null) {
      fields.push({ label: "Asset Linking", value: instrument.attributes.asset_blocked ? "Blocked" : "Allowed" })
    }
  }

  return (
    <ResourceDetail
      title={instrument?.attributes.name || "Instrument"}
      subtitle={instrument_id}
      loading={!instrument}
      fields={fields}
      links={[]}
    >
      {instrument?.attributes.asset_blocked == null ? null : (
        <VStack className="gap-2">
          {error ? (
            <Box className="rounded-md border border-border-medium bg-background-tertiary px-3 py-2">
              <Text className="text-sm text-foreground-secondary whitespace-pre-wrap">{error}</Text>
            </Box>
          ) : null}

          <HStack>
            <Button
              onPress={toggleAssetBlocked}
              isDisabled={saving}
            >
              <ButtonText className="text-foreground-primary">
                {instrument.attributes.asset_blocked ? "Unblock Asset Linking" : "Block Asset Linking"}
              </ButtonText>
            </Button>
          </HStack>
        </VStack>
      )}

      <AssetLinkModal
        isOpen={linkOpen}
        onClose={() => setLinkOpen(false)}
        onLink={linkAsset}
      />
    </ResourceDetail>
  )
}
